import { ReportFileType } from '../generated'
import { apiFetch } from '../app/utils/apiRequest'

/** A voucher paired with the check leaf the operator approved it for, in the confirmation table. */
export interface ApprovedCheckAssignment {
  voucherNo: string
  checkNo?: string | null
}

export async function printChecks(
  vouchers: string[],
  preview: boolean,
  startingCheckNo?: string,
  bankAccountId?: string,
  // Sent back so the API can refuse the run outright if its own plan no longer matches what was on
  // screen, rather than printing a different set of numbers than the ones that were reviewed.
  assignments?: ApprovedCheckAssignment[]
): Promise<void> {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/checks/print`, {
    method: 'POST',
    body: JSON.stringify({
      vouchers,
      isPreview: preview,
      startingCheckNo,
      bankAccountId,
      assignments
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to generate PDF')
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

export async function printTrustFundsCheck(paymentId: string): Promise<void> {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/checks/print-trust-funds-check`, {
    method: 'POST',
    body: JSON.stringify({ paymentId })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to generate PDF')
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

export async function printRefundCheck(paymentId: string): Promise<void> {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/checks/print-refund-check`, {
    method: 'POST',
    body: JSON.stringify({ paymentId })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to generate PDF')
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

export async function printClientSettlementCheck(caseId: string, clientId: string): Promise<void> {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/checks/print-client-settlement-check`, {
    method: 'POST',
    body: JSON.stringify({ caseId, clientId })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to generate PDF')
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

/** Mirrors the API's `ExportGeneralLedgerInput`. Dates must be `YYYY-MM-DD`. */
export interface ExportLedgerInput {
  from: string
  to: string
  type: ReportFileType
  /** Optional single-account filter; omit to export the whole chart of accounts. */
  accountId?: string
}

export async function exportLedger({ from, to, type, accountId }: ExportLedgerInput): Promise<void> {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/ledger/export`, {
    method: 'POST',
    body: JSON.stringify({
      from,
      to,
      type,
      accountId
    })
  })

  if (!response.ok) {
    const error = await response.json()
    // Nest's ValidationPipe returns `message` as a string[] of constraint failures.
    const message = Array.isArray(error?.message) ? error.message.join(', ') : error?.message
    throw new Error(message || `Failed to export ${type}`)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  if (type === ReportFileType.Csv) {
    // Trigger CSV download
    const a = document.createElement('a')
    a.href = url
    a.download = 'general_ledger.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } else {
    // Open the PDF in a new tab
    window.open(url, '_blank')
  }
}

/** Mirrors the API's `ExportClientLedgerInput`. Dates must be `YYYY-MM-DD`. */
export interface ExportClientLedgerInput {
  /** Required — the Client Ledger is always one matter's ledger. */
  matterId: string
  /** Both ends or neither; omit for the matter's entire lifecycle. */
  from?: string
  to?: string
  entryType?: string
  type: ReportFileType
}

export async function exportClientLedger({
  matterId,
  from,
  to,
  entryType,
  type
}: ExportClientLedgerInput): Promise<void> {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/client-ledger/export`, {
    method: 'POST',
    headers: {
      // The PDF header prints the generated-at time; send the viewer's zone so it reads local.
      'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    body: JSON.stringify({ matterId, from, to, entryType, type })
  })

  if (!response.ok) {
    const error = await response.json()
    // Nest's ValidationPipe returns `message` as a string[] of constraint failures.
    const message = Array.isArray(error?.message) ? error.message.join(', ') : error?.message
    throw new Error(message || `Failed to export ${type}`)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)

  if (type === ReportFileType.Csv) {
    const a = document.createElement('a')
    a.href = url
    a.download = 'client_ledger.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } else {
    window.open(url, '_blank')
  }
}

/**
 * Whether the current browser can reliably trigger the native print dialog for
 * a PDF embedded in a hidden iframe. Only Chromium-based browsers (Chrome,
 * Edge, Brave, Opera) support this; Firefox and Safari render a blank or broken
 * job, so those must fall back to opening the PDF in a new tab instead.
 */
const supportsIframePdfPrint = (): boolean => {
  return false // TODO: fix this
  if (typeof navigator === 'undefined') return false

  // Prefer the modern, spoof-resistant signal when the browser exposes it.
  const uaData = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } }).userAgentData
  if (uaData?.brands?.length) {
    // return uaData.brands.some(b => /Chromium|Google Chrome|Microsoft Edge/i.test(b.brand))
  }

  const ua = navigator.userAgent || ''
  if (/Firefox\//i.test(ua)) return false
  // Chromium family: Chrome/Chromium, Edge ('Edg'), iOS Chrome ('CriOS').
  // Safari is intentionally excluded (has 'Safari' but none of these tokens).
  return /Chrome\/|Chromium\/|CriOS\/|Edg\//i.test(ua)
}

/**
 * Loads a PDF blob into a hidden iframe and immediately triggers the browser's
 * native print dialog, so the user goes straight from "Print" to the print
 * preview screen without an intermediate PDF tab.
 *
 * Falls back to opening the PDF in a new tab when in-place printing isn't
 * available (e.g. a browser that can't print a PDF embedded in an iframe).
 */
const printPdfBlob = (blob: Blob): void => {
  const url = URL.createObjectURL(blob)

  // Keep the object URL alive — the new tab needs it to render the PDF.
  const openInTab = () => {
    window.open(url, '_blank')
  }

  // Only Chromium reliably prints a PDF from a hidden iframe; everywhere else
  // (Firefox, Safari, SSR) fall back to opening the PDF in a new tab.
  if (typeof document === 'undefined' || !supportsIframePdfPrint()) {
    openInTab()
    return
  }

  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.setAttribute('aria-hidden', 'true')

  let printed = false
  let cleaned = false
  let loadTimeout: ReturnType<typeof setTimeout>

  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    // Delay teardown so the print dialog keeps a valid document underneath it.
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
      URL.revokeObjectURL(url)
    }, 1000)
  }

  iframe.onload = () => {
    if (printed) return
    printed = true
    clearTimeout(loadTimeout)
    try {
      const frameWindow = iframe.contentWindow
      if (!frameWindow) throw new Error('No iframe window')
      // Tear down only after the print dialog closes. Revoking the blob URL or
      // removing the iframe while the browser is still spooling the job or
      // writing a Save-as-PDF file yields a corrupt PDF — so never do it on a
      // blind timer. Chrome fires afterprint on the iframe or the top window
      // depending on version, so we listen on both.
      frameWindow.addEventListener('afterprint', cleanup)
      window.addEventListener('afterprint', cleanup, { once: true })
      frameWindow.focus()
      frameWindow.print()
    } catch {
      // In-place printing not available — open the PDF in a new tab instead.
      // Leave the object URL alive so the tab can render it.
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
      openInTab()
    }
  }

  iframe.onerror = () => {
    if (printed) return
    printed = true
    clearTimeout(loadTimeout)
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    openInTab()
  }

  document.body.appendChild(iframe)

  // Watchdog: if the PDF never loads (onload/onerror never fire), fall back to
  // opening it in a new tab so the user is never left with nothing.
  loadTimeout = setTimeout(() => {
    if (printed) return
    printed = true
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    openInTab()
  }, 10000)

  iframe.src = url
}

export async function printBills(billIds: number[]): Promise<void> {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/notifications/print-bills`, {
    method: 'POST',
    body: JSON.stringify({
      billIds
    })
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to generate PDF')
  }

  const contentType = response.headers.get('Content-Type') || ''
  if (!contentType.toLowerCase().includes('application/pdf')) {
    const text = await response.text()
    let message = 'Server did not return a PDF'
    try {
      const json = JSON.parse(text)
      if (json.message) message = json.message
    } catch {
      if (text.length < 200) message = text || message
    }
    throw new Error(message)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

interface PrintBillOptions {
  asPreBill?: boolean
  /** Mark the printed (issued) bills as delivered to the client. Ignored for pre-bill prints. */
  markAsDelivered?: boolean
}

const printBill = async (billIds: number[], options?: PrintBillOptions): Promise<Blob> => {
  const response = await apiFetch(`${process.env.REACT_APP_API_BASE_URL}/notifications/print-bills-v2`, {
    method: 'POST',
    body: JSON.stringify({
      billIds,
      ...(options?.asPreBill ? { asPreBill: true } : {}),
      ...(options?.markAsDelivered ? { markAsDelivered: true } : {})
    })
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to generate PDF')
  }

  const contentType = response.headers.get('Content-Type') || ''
  if (!contentType.toLowerCase().includes('application/pdf')) {
    const text = await response.text()
    let message = 'Server did not return a PDF'
    try {
      const json = JSON.parse(text)
      if (json.message) message = json.message
    } catch {
      if (text.length < 200) message = text || message
    }
    throw new Error(message)
  }

  return await response.blob()
}

export async function printBillsV2(billIds: number[], options?: PrintBillOptions): Promise<void> {
  const blob = await printBill(billIds, options)
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
}

export async function printBillV2(billIds: number[], options?: PrintBillOptions): Promise<void> {
  const blob = await printBill(billIds, options)
  printPdfBlob(blob)
}
